import { useEffect, useState } from 'react';
import DataStoreAuditLogService, {
    AuditLogParams,
} from '../../services/DataStoreAuditLogService';
import { AuditLogLine } from '../../types/AuditLog';

const AUDIT_LOG_LIMIT = 10;

export default function useDataStoreAuditLog(dataStoreUuid?: string) {
    const [lines, setLines] = useState<AuditLogLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [from, setFrom] = useState<string | undefined>(undefined);
    const [to, setTo] = useState<string | undefined>(undefined);

    const load = async (fromFilter?: string, toFilter?: string) => {
        if (!dataStoreUuid) return;
        setLoading(true);
        try {
            const params: AuditLogParams = { limit: AUDIT_LOG_LIMIT };
            if (fromFilter) params.from = fromFilter;
            if (toFilter) params.to = toFilter;
            const result = await DataStoreAuditLogService.getDataStoreAuditLog(
                dataStoreUuid,
                params
            );
            setLines(result.lines);
            setHasMore(result.lines.length === AUDIT_LOG_LIMIT);
            setError(null);
        } catch (e: any) {
            setError(e);
        }
        setLoading(false);
    };

    const loadMore = async () => {
        if (!dataStoreUuid || loadingMore || lines.length === 0) return;
        setLoadingMore(true);
        try {
            const oldest = lines.reduce((a, b) =>
                new Date(a.time) < new Date(b.time) ? a : b
            );
            const cursorTo = new Date(
                new Date(oldest.time).getTime() - 1
            ).toISOString();
            const params: AuditLogParams = {
                to: cursorTo,
                limit: AUDIT_LOG_LIMIT,
            };
            if (from) params.from = from;
            const result = await DataStoreAuditLogService.getDataStoreAuditLog(
                dataStoreUuid,
                params
            );
            setLines((prev) => [...prev, ...result.lines]);
            setHasMore(result.lines.length === AUDIT_LOG_LIMIT);
        } catch (e: any) {
            setError(e);
        }
        setLoadingMore(false);
    };

    const setFilter = (newFrom?: string, newTo?: string) => {
        setFrom(newFrom);
        setTo(newTo);
    };

    const clearFilters = () => {
        setFrom(undefined);
        setTo(undefined);
    };

    useEffect(() => {
        load(from, to);
    }, [dataStoreUuid, from, to]);

    return {
        lines,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        error,
        from,
        to,
        setFilter,
        clearFilters,
    };
}
